/* oxlint-disable jsx-a11y/no-noninteractive-tabindex -- focusable overflow lets keyboard users scroll the timeline */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useVirtualizer } from '@tanstack/react-virtual'
import type { ShiftTimeline as ShiftTimelineData } from '@transreport/pauk-rrms-api'
import {
  assignCallsignToAssistanceDetail,
  getShiftTimeline,
  useGetStationSetting,
  unassignCallsignFromAssistanceDetail,
  updateJobTiming,
} from '@transreport/pauk-rrms-api'
import { format, isSameDay, parse, parseISO } from 'date-fns'
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Link2,
  MapPin,
  Pause,
  Play,
  Search,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { AppButton, AppTooltip, Modal } from '@/components/framework'
import { AddBreakModal } from '@/components/modules/timeline/AddBreakModal'
import { AssistanceDetailsModal } from '@/components/modules/timeline/AssistanceDetailsModal'
import { RequestDetailModal } from '@/components/modules/timeline/RequestDetailModal'
import { ShiftTimelineSkeleton } from '@/components/modules/timeline/ShiftTimelineSkeleton'
import { LinkedJobsContext } from '@/components/shared/LinkedJobsContext'
import { useAlarmSound } from '@/hooks/useAlarmSound'
import { useOperatingStations } from '@/hooks/useOperatingStations'
import { useStationAlarmsChannel } from '@/hooks/useStationAlarmsChannel'
import { trackEvent, trackOutcome } from '@/lib/analytics'
import { getApiErrorMessage } from '@/lib/apiErrors'
import { defineMutationAnalytics } from '@/lib/queryClient'
import { useShiftTimelineStore } from '@/lib/stores/shiftTimeline'
import {
  DAY_MINUTES,
  MAX_WINDOW_SPAN_MINUTES,
  MIN_WINDOW_SPAN_MINUTES,
  buildLaneCrossovers,
  buildRowLayout,
  clamp,
  clampMovedWindow,
  clampResizedEnd,
  clampResizedStart,
  clampWindowEnd,
  clampWindowStart,
  determineDragAxis,
  minutesToHHmm,
  parseTimeToMinutes,
  pixelsToMinutes,
  sortShiftRows,
  shouldShowNowLine,
  shouldShowRequestConflictStyling,
} from '@/lib/timeline'
import {
  getAssignmentValidationError,
  getAssignmentValidationMessageParams,
  isCallsignOnShift,
  isRequestWithinCallsignHours,
} from '@/lib/timeline/assignmentValidation'
import { UNASSIGNED_ROW_ID } from '@/lib/timeline/constants'
import {
  getRealtimeSeverity,
  hasPlatformChangeSignal,
  isJourneyLate,
} from '@/lib/timeline/realtimeClassification'
import { applyRequestUpdate, shiftTimelineToCallsignRows } from '@/lib/timeline/shiftTimelineModel'
import type { Request } from '@/types/timeline'

import './TimelinePage.css'

type AssignCallsignMutationVariables = {
  callsignId: string
  assistanceDetailId: string
  isReassignment: boolean
  assignmentMethod: 'drag_drop' | 'modal'
}
type UpdateRequestTimingMutationVariables = {
  requestId: string
  startTime: string
  endTime: string
  interactionMethod: 'drag_drop' | 'modal'
}

const MINIMAP_TICKS = Array.from({ length: Math.floor(DAY_MINUTES / 120) + 1 }, (_, i) => i * 120)

const ROW_LANE_HEIGHT = 76
const JOB_PADDING = 7
// pixels of movement required before a pointer-down is treated as a drag (vs. a click)
const DRAG_THRESHOLD = 5
const UNASSIGNED_PREVIEW_LIMIT = 3
const UNASSIGNED_QUEUE_BATCH_SIZE = 50
const EMPTY_REQUESTS: Request[] = []

const getRequestStationCode = (label: string): string => {
  const parts = label.split('-').map((part) => part.trim())
  return parts.length > 1 ? parts[1] : parts[0]
}

type RequestOverride = {
  rowId: string
  startMinutes: number
  endMinutes: number
}

type DragMode = 'move' | 'resize-left' | 'resize-right'
type ScrubberDragMode = 'pan' | 'resize-left' | 'resize-right'
type TimelineViewMode = 'visual' | 'list'

type ActiveDrag = {
  requestId: string
  mode: DragMode
  axis: 'horizontal' | 'vertical' | null
  lockedAxis?: 'vertical'
  startClientX: number
  startClientY: number
  contentWidth: number
  originRowId: string
  originStartMinutes: number
  originEndMinutes: number
  anchorMinutes: number
  requestType: Request['type']
  draftRowId: string
  draftStartMinutes: number
  draftEndMinutes: number
  moved: boolean
  shiftStartMinutes: number | null
  shiftEndMinutes: number | null
}

const toApiDateTime = (date: Date, time: string): string =>
  format(parse(time, 'HH:mm', date), "yyyy-MM-dd'T'HH:mm:ssxxx")

const RequestTooltipContent = ({ request }: { request: Request }) => {
  const { t } = useTranslation()
  const startMins = parseTimeToMinutes(request.startTime)
  const endMins = parseTimeToMinutes(request.endTime)
  const durationMins = startMins != null && endMins != null ? endMins - startMins : null
  const durationStr =
    durationMins != null && durationMins > 0
      ? durationMins < 60
        ? `${durationMins}m`
        : durationMins % 60 === 0
          ? `${Math.floor(durationMins / 60)}h`
          : `${Math.floor(durationMins / 60)}h ${durationMins % 60}m`
      : null

  const severity = getRealtimeSeverity(request.realtimeClassification)
  const hasRealtimeAlert = severity !== 'normal'
  const realtimeTime = request.type === 'DEP' ? request.realtimeDeparture : request.realtimeArrival

  const RealtimeIcon = severity === 'delayed' ? Clock : AlertCircle
  const realtimeColour =
    severity === 'delayed'
      ? 'text-amber-300'
      : severity === 'disrupted' || severity === 'cancelled'
        ? 'text-red-300'
        : ''
  const classificationLabel = request.realtimeClassification
    ? t(`realtimeClassification.${request.realtimeClassification}`, { defaultValue: '' })
    : ''
  const passengerName = request.customerName ?? t('timeline.unknownPassenger')
  const requestRef = request.ref ?? request.id

  return (
    <div className="max-w-110 min-w-75 space-y-1.5">
      <div className="flex items-start justify-between gap-3 pb-0.5">
        <div className="min-w-0 space-y-1">
          <div className="truncate text-sm font-bold text-white">{passengerName}</div>
          <div className="text-xs font-semibold text-slate-400">
            {t('timeline.tooltip.timeRange')}
          </div>
          <div className="truncate text-sm font-bold text-white">
            {request.startTime} – {request.endTime}
          </div>
        </div>
        <span
          className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold ${
            request.type === 'ARR'
              ? 'bg-arrival-bg text-arrival-text'
              : 'bg-departure-bg text-departure-text'
          }`}
        >
          {request.type}
        </span>
      </div>
      <div className="flex items-start justify-between gap-3 pb-0.5">
        <div className="min-w-0 space-y-0.5">
          <div className="truncate text-xs text-slate-400">{requestRef}</div>
        </div>
      </div>
      {durationStr && (
        <div className="flex justify-between gap-4 text-sm text-slate-200">
          <span>{t('timeline.tooltip.duration')}</span>
          <span>{durationStr}</span>
        </div>
      )}
      {request.scheduledTime && (
        <>
          <div className="flex items-center justify-between gap-4 text-sm text-slate-200">
            <span className="shrink-0">{t('timeline.tooltip.scheduled')}</span>
            {hasRealtimeAlert && realtimeTime ? (
              <span className="flex items-center gap-1.5">
                <span className="text-slate-500 line-through">{request.scheduledTime}</span>
                <RealtimeIcon size={14} aria-hidden="true" className={realtimeColour} />
                <span className={`font-bold ${realtimeColour}`}>{realtimeTime}</span>
              </span>
            ) : (
              <span>{request.scheduledTime}</span>
            )}
          </div>
          {hasRealtimeAlert && realtimeTime && classificationLabel && (
            <div className={`-mt-1 flex justify-end text-xs font-semibold ${realtimeColour}`}>
              {classificationLabel}
            </div>
          )}
        </>
      )}
      <div className="flex items-center justify-between gap-4 text-sm text-slate-300">
        <span>{t('timeline.tooltip.platform')}</span>
        <span className="flex items-center gap-1.5">
          <span>{request.platform ?? '–'}</span>
          {hasRealtimeAlert && request.platform && (
            <MapPin size={14} aria-hidden="true" className={realtimeColour} />
          )}
        </span>
      </div>
      {request.headCode && (
        <div className="flex justify-between gap-4 text-sm text-slate-300">
          <span>{t('timeline.tooltip.headcode')}</span>
          <span>{request.headCode}</span>
        </div>
      )}
      {request.assistanceDetails && (
        <div className="flex justify-between gap-4 text-sm text-slate-300">
          <span className="shrink-0">{t('assistanceDetails.assistanceTypes')}</span>
          <span className="text-right">{request.assistanceDetails}</span>
        </div>
      )}
      {request.additionalInfo && (
        <div className="text-sm text-slate-300">{request.additionalInfo}</div>
      )}
      {request.linkedJobContext && (
        <div className="mt-2 border-t border-slate-600 pt-2">
          <LinkedJobsContext context={request.linkedJobContext} />
        </div>
      )}
    </div>
  )
}

const RequestRealtimeIndicators = ({ request }: { request: Request }) => {
  const { t } = useTranslation()
  const showLate = isJourneyLate(request.realtimeClassification)
  const showPlatformChange = hasPlatformChangeSignal(
    request.realtimeClassification,
    request.platform
  )

  if (!showLate && !showPlatformChange) return null

  return (
    <span className="stp-job-indicators">
      {showLate && (
        <span
          className="stp-job-indicator stp-job-indicator-late"
          aria-label={t('journeyIndicators.lateAria')}
        >
          {t('journeyIndicators.lateShort')}
        </span>
      )}
      {showPlatformChange && (
        <span
          className="stp-job-indicator stp-job-indicator-platform"
          aria-label={t('journeyIndicators.platformChangedAria')}
        >
          {t('journeyIndicators.platformChangedShort')}
        </span>
      )}
    </span>
  )
}

const BreakTooltipContent = ({
  startTime,
  endTime,
  reason,
}: {
  startTime: string
  endTime: string
  reason?: string | null
}) => {
  const { t } = useTranslation()
  const trimmed = reason?.trim()

  return (
    <div className="max-w-65 min-w-35">
      <div className="mb-1 font-bold text-white">{t('shiftTimeline.breakTooltip.title')}</div>
      <div className="text-slate-300">
        {t('shiftTimeline.breakTooltip.timeRange', { start: startTime, end: endTime })}
      </div>
      {trimmed ? (
        <div className="mt-1.5 text-slate-400">
          <span className="text-slate-300">{t('shiftTimeline.breakTooltip.reason')}: </span>
          {trimmed}
        </div>
      ) : null}
    </div>
  )
}

interface TimelinePageProps {
  prototypeLayout?: boolean
}

export const TimelinePage = ({ prototypeLayout = false }: TimelinePageProps) => {
  const { t } = useTranslation()
  const { selectedStationCode } = useOperatingStations()

  const rowContentRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const rowElementRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const rowsScrollRef = useRef<HTMLDivElement | null>(null)
  const rowsScrollResizeObserverRef = useRef<ResizeObserver | null>(null)
  const dragLaneFloorRef = useRef<Record<string, number>>({})
  const activeDragRef = useRef<ActiveDrag | null>(null)
  const suppressNextClickByRequestIdRef = useRef<Record<string, boolean>>({})
  const scrubberTrackRef = useRef<HTMLDivElement | null>(null)

  const [selectedDateValue, setSelectedDateValue] = useState(() => format(new Date(), 'yyyy-MM-dd'))
  const [filterDateValue, setFilterDateValue] = useState(() => format(new Date(), 'yyyy-MM-dd'))
  const [searchQuery, setSearchQuery] = useState('')
  const [filterQuery, setFilterQuery] = useState<string | undefined>(undefined)
  const [isUnassignedQueueOpen, setIsUnassignedQueueOpen] = useState(prototypeLayout)
  const [unassignedQueueQuery, setUnassignedQueueQuery] = useState('')
  const [unassignedQueueLimit, setUnassignedQueueLimit] = useState(UNASSIGNED_QUEUE_BATCH_SIZE)

  const [windowStart, setWindowStart] = useState(7 * 60)
  const [windowEnd, setWindowEnd] = useState(7 * 60 + MAX_WINDOW_SPAN_MINUTES)
  const [requestOverrides, setRequestOverrides] = useState<Record<string, RequestOverride>>({})
  const [draggingRequestId, setDraggingRequestId] = useState<string | null>(null)
  const [dropTargetRowId, setDropTargetRowId] = useState<string | null>(null)
  const [collapsedRowIds, setCollapsedRowIds] = useState<Set<string>>(() => new Set())
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null)
  const hideOffShiftCallsigns = useShiftTimelineStore((state) => state.hideOffShiftCallsigns)
  const setHideOffShiftCallsigns = useShiftTimelineStore((state) => state.setHideOffShiftCallsigns)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)
  const [assignedCallsignName, setAssignedCallsignName] = useState<string | undefined>()
  const [isAddBreakModalOpen, setIsAddBreakModalOpen] = useState(false)
  const [editingBreak, setEditingBreak] = useState<null | {
    id: string
    callsignId: string
    startTime: string
    endTime: string
    reason?: string | null
  }>(null)
  const [isPaused, setIsPaused] = useState(false)
  const [currentNow, setCurrentNow] = useState(() => new Date())
  const [scrollbarWidth, setScrollbarWidth] = useState(0)
  const [activeLinkedBookingId, setActiveLinkedBookingId] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<TimelineViewMode>(() =>
    typeof window !== 'undefined' && window.matchMedia?.('(max-width: 640px)').matches
      ? 'list'
      : 'visual'
  )
  const [timingEditor, setTimingEditor] = useState<null | {
    requestId: string
    requestRef: string
    callsignId: string
    startTime: string
    endTime: string
  }>(null)
  const [timingError, setTimingError] = useState('')

  const setLaneCollapsed = (rowId: string, collapsed: boolean) =>
    setCollapsedRowIds((previous) => {
      if (previous.has(rowId) === collapsed) return previous
      const next = new Set(previous)
      if (collapsed) next.add(rowId)
      else next.delete(rowId)
      return next
    })

  const { unassignedAlarms } = useStationAlarmsChannel(selectedStationCode)
  const { playAlarmSound } = useAlarmSound()
  const alarmToastIds = useRef<Map<string, string | number>>(new Map())

  // Sync persistent toasts with the current alarm list.
  useEffect(() => {
    const incomingIds = new Set(unassignedAlarms.map((a) => a.id))

    // Dismiss cleared alarms.
    for (const [id, toastId] of alarmToastIds.current) {
      if (!incomingIds.has(id)) {
        toast.dismiss(toastId)
        alarmToastIds.current.delete(id)
      }
    }

    // Play sound once if any new alarms are in this batch.
    const hasNewAlarms = unassignedAlarms.some((a) => !alarmToastIds.current.has(a.id))
    if (hasNewAlarms) playAlarmSound()

    // Show new alarms.
    for (const alarm of unassignedAlarms) {
      if (alarmToastIds.current.has(alarm.id)) continue

      const toastId = toast.warning(
        `${t('shiftTimeline.alarm.title')}: ${alarm.booking.ref} (${t(`shiftTimeline.alarm.category.${alarm.category}`)}, ${format(parseISO(alarm.start_time), 'HH:mm')})`,
        { duration: Number.POSITIVE_INFINITY }
      )
      alarmToastIds.current.set(alarm.id, toastId)
    }
  }, [unassignedAlarms, t, playAlarmSound])

  // Dismiss all active alarm toasts on unmount.
  useEffect(() => {
    const toastIds = alarmToastIds.current
    return () => {
      for (const toastId of toastIds.values()) {
        toast.dismiss(toastId)
      }
    }
  }, [])

  useEffect(() => {
    if (!window.matchMedia) return
    const mediaQuery = window.matchMedia('(max-width: 640px)')
    const handleViewportChange = (event: MediaQueryListEvent) => {
      setViewMode(event.matches ? 'list' : 'visual')
    }
    mediaQuery.addEventListener?.('change', handleViewportChange)
    return () => mediaQuery.removeEventListener?.('change', handleViewportChange)
  }, [])

  const filterDate = useMemo(
    () => parse(filterDateValue, 'yyyy-MM-dd', new Date()),
    [filterDateValue]
  )
  const selectedDateParam = format(filterDate, 'dd-MM-yyyy')
  const queryClient = useQueryClient()

  const shiftTimelineQueryKey = [
    'timeline',
    selectedDateParam,
    selectedStationCode,
    filterQuery,
  ] as const

  const stationSettingQuery = useGetStationSetting(selectedStationCode, {
    query: { enabled: Boolean(selectedStationCode) },
  })
  const autoUpdateAssistanceJobTime =
    stationSettingQuery.data?.auto_update_assistance_job_time ?? false

  const resizeDurationBounds = useMemo(() => {
    const defaultDurationSeconds = stationSettingQuery.data?.default_assistance_job_duration
    if (!defaultDurationSeconds || defaultDurationSeconds <= 0) {
      return undefined
    }

    const defaultDurationMinutes = Math.round(defaultDurationSeconds / 60)
    return {
      minDuration: Math.max(1, defaultDurationMinutes - 15),
      maxDuration: defaultDurationMinutes + 15,
    }
  }, [stationSettingQuery.data?.default_assistance_job_duration])

  const timelineQuery = useQuery({
    queryKey: shiftTimelineQueryKey,
    queryFn: () =>
      getShiftTimeline({
        date: selectedDateParam,
        station_code: selectedStationCode,
        query: filterQuery,
      }),
    enabled: Boolean(selectedStationCode),
    refetchInterval: isPaused ? false : 10_000,
    staleTime: 0,
  })

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentNow(new Date())
    }, 60_000)

    return () => window.clearInterval(intervalId)
  }, [])

  const callsigns = useMemo(() => {
    const rows = shiftTimelineToCallsignRows(timelineQuery.data ?? null, selectedStationCode, {
      autoUpdateAssistanceJobTime,
    })
    const unassignedRow = rows.find((row) => row.id === UNASSIGNED_ROW_ID)
    const assignedRows = rows.filter((row) => row.id !== UNASSIGNED_ROW_ID)
    const sortedRows = sortShiftRows(assignedRows, (row) => ({
      id: row.id,
      name: row.name,
      startTime: row.shiftStartTime,
      endTime: row.shiftEndTime,
    }))
    return unassignedRow ? [...sortedRows, unassignedRow] : sortedRows
  }, [autoUpdateAssistanceJobTime, selectedStationCode, timelineQuery.data])

  const assignCallsignMutation = useMutation({
    mutationKey: ['timeline/assignCallsign'],
    meta: {
      analytics: defineMutationAnalytics<
        AssignCallsignMutationVariables,
        'request_assigned' | 'request_reassigned'
      >({
        event: (variables) =>
          variables.isReassignment ? 'request_reassigned' : 'request_assigned',
        area: 'timeline',
        parameters: (variables) => ({ assignment_method: variables.assignmentMethod }),
      }),
    },
    mutationFn: (variables: AssignCallsignMutationVariables) =>
      assignCallsignToAssistanceDetail(variables.callsignId, {
        assistance_detail_id: variables.assistanceDetailId,
      }),
    onSuccess: (_data, { callsignId, assignmentMethod }) => {
      if (assignmentMethod === 'drag_drop') setLaneCollapsed(callsignId, false)
    },
    onError: async (error) => {
      const fallback = t('requestAssign.assignError')
      toast.error((await getApiErrorMessage(error)) ?? fallback)
    },
  })

  const unassignCallsignMutation = useMutation({
    mutationKey: ['timeline/unassignCallsign'],
    mutationFn: ({
      callsignId,
      assistanceDetailId,
    }: {
      callsignId: string
      assistanceDetailId: string
    }) =>
      unassignCallsignFromAssistanceDetail(callsignId, {
        assistance_detail_id: assistanceDetailId,
      }),
    meta: {
      analytics: { event: 'request_unassigned', area: 'timeline' },
      toast: { error: t('requestAssign.unassignError') },
    },
  })

  const updateRequestTimingMutation = useMutation({
    mutationKey: ['timeline/updateJobTiming'],
    meta: {
      analytics: defineMutationAnalytics<
        UpdateRequestTimingMutationVariables,
        'request_timing_updated'
      >({
        event: 'request_timing_updated',
        area: 'timeline',
        parameters: (variables) => ({ interaction_method: variables.interactionMethod }),
      }),
    },
    mutationFn: ({ requestId, startTime, endTime }: UpdateRequestTimingMutationVariables) =>
      updateJobTiming(requestId, {
        start_time: toApiDateTime(filterDate, startTime),
        end_time: toApiDateTime(filterDate, endTime),
        action_origin: 'web_dashboard',
      }),
    onError: async (error) => {
      const fallback = t('shiftTimeline.requestMoveRejected')
      toast.error((await getApiErrorMessage(error)) ?? fallback)
    },
  })

  const refetchTimeline = () => {
    const pendingMutations =
      queryClient.isMutating({ mutationKey: ['timeline/updateJobTiming'] }) +
      queryClient.isMutating({ mutationKey: ['timeline/assignCallsign'] }) +
      queryClient.isMutating({ mutationKey: ['timeline/unassignCallsign'] })

    if (pendingMutations <= 1) {
      queryClient.invalidateQueries({ queryKey: shiftTimelineQueryKey })
    }
  }

  const commitRequestUpdate = (
    requestId: string,
    nextStartMinutes: number,
    nextEndMinutes: number,
    nextRowId: string,
    assignmentMethod?: 'drag_drop' | 'modal'
  ) => {
    const sourceCallsign = callsigns.find((callsign) =>
      callsign.requests.some((r) => r.id === requestId)
    )
    const sourceCallsignId = sourceCallsign?.id
    const selectedTimelineRequest = callsigns
      .flatMap((callsign) => callsign.requests)
      .find((r) => r.id === requestId)

    if (!selectedTimelineRequest) return

    const startTime = minutesToHHmm(nextStartMinutes)
    const endTime = minutesToHHmm(nextEndMinutes)

    const applyOptimisticRequestUpdate = (updates: {
      startTime?: string
      endTime?: string
      newCallsignId?: string
    }) => {
      const previousData = queryClient.getQueryData<ShiftTimelineData | undefined>(
        shiftTimelineQueryKey
      )

      const rollbackOptimisticUpdate = () => {
        queryClient.setQueryData<ShiftTimelineData | undefined>(shiftTimelineQueryKey, previousData)
      }

      queryClient.setQueryData<ShiftTimelineData | undefined>(
        shiftTimelineQueryKey,
        (currentData) => {
          if (!currentData) {
            return currentData
          }

          return applyRequestUpdate(currentData, requestId, updates)
        }
      )

      return rollbackOptimisticUpdate
    }

    const dismissAlarmForRequest = (id: string) => {
      const toastId = alarmToastIds.current.get(id)
      if (toastId !== undefined) {
        toast.dismiss(toastId)
        alarmToastIds.current.delete(id)
      }
    }

    if (nextRowId !== sourceCallsignId) {
      if (nextRowId === UNASSIGNED_ROW_ID) {
        if (!sourceCallsignId || sourceCallsignId === UNASSIGNED_ROW_ID) return

        const rollbackOptimisticUpdate = applyOptimisticRequestUpdate({
          newCallsignId: UNASSIGNED_ROW_ID,
        })
        unassignCallsignMutation.mutate(
          {
            callsignId: sourceCallsignId,
            assistanceDetailId: requestId,
          },
          {
            onError: rollbackOptimisticUpdate,
            onSettled: refetchTimeline,
          }
        )
        return
      }

      const targetCallsign = callsigns.find((callsign) => callsign.id === nextRowId)
      if (!targetCallsign) return

      const validationError = getAssignmentValidationError(selectedTimelineRequest, targetCallsign)
      if (validationError) {
        const eventName =
          sourceCallsignId && sourceCallsignId !== UNASSIGNED_ROW_ID
            ? 'request_reassigned'
            : 'request_assigned'
        trackOutcome(eventName, 'timeline', 'attempted', {
          assignment_method: assignmentMethod,
        })
        trackOutcome(eventName, 'timeline', 'failed', {
          failure_type: 'validation',
          assignment_method: assignmentMethod,
        })
        toast.error(
          t(
            `assignmentValidation.${validationError}`,
            getAssignmentValidationMessageParams(selectedTimelineRequest, targetCallsign)
          )
        )
        return
      }

      dismissAlarmForRequest(requestId)
      const rollbackOptimisticUpdate = applyOptimisticRequestUpdate({ newCallsignId: nextRowId })
      assignCallsignMutation.mutate(
        {
          callsignId: nextRowId,
          assistanceDetailId: requestId,
          isReassignment: Boolean(sourceCallsignId && sourceCallsignId !== UNASSIGNED_ROW_ID),
          assignmentMethod: assignmentMethod ?? 'modal',
        },
        {
          onError: rollbackOptimisticUpdate,
          onSettled: refetchTimeline,
        }
      )
      return
    }

    if (
      startTime === selectedTimelineRequest.startTime &&
      endTime === selectedTimelineRequest.endTime
    ) {
      return
    }

    if (sourceCallsign && sourceCallsignId !== UNASSIGNED_ROW_ID) {
      const resizedRequest = { id: requestId, startTime, endTime }
      if (!isRequestWithinCallsignHours(resizedRequest, sourceCallsign)) {
        trackOutcome('request_timing_updated', 'timeline', 'attempted', {
          interaction_method: assignmentMethod ?? 'modal',
        })
        trackOutcome('request_timing_updated', 'timeline', 'failed', {
          failure_type: 'validation',
          interaction_method: assignmentMethod ?? 'modal',
        })
        toast.error(
          t(
            'assignmentValidation.outsideWorkingHours',
            getAssignmentValidationMessageParams(resizedRequest, sourceCallsign)
          )
        )
        return
      }
    }

    const rollbackOptimisticUpdate = applyOptimisticRequestUpdate({ startTime, endTime })
    updateRequestTimingMutation.mutate(
      {
        requestId,
        startTime,
        endTime,
        interactionMethod: assignmentMethod ?? 'modal',
      },
      {
        onError: rollbackOptimisticUpdate,
        onSettled: refetchTimeline,
      }
    )
  }

  const handleRequestClick = (requestId: string) => {
    if (suppressNextClickByRequestIdRef.current[requestId]) {
      delete suppressNextClickByRequestIdRef.current[requestId]
      return
    }

    const request = callsigns
      .flatMap((callsign) => callsign.requests)
      .find((item) => item.id === requestId)
    if (!request) return

    const assignedCallsign = callsigns.find((c) => c.requests.some((r) => r.id === requestId))
    setAssignedCallsignName(
      assignedCallsign?.id !== UNASSIGNED_ROW_ID ? assignedCallsign?.name : undefined
    )
    setSelectedRequest(request)
    setIsDetailsModalOpen(true)
    trackEvent('request_opened', { area: 'timeline' })
  }

  const handleOpenTimingEditor = (request: Request, callsignId: string) => {
    setTimingError('')
    setTimingEditor({
      requestId: request.id,
      requestRef: request.ref ?? request.id,
      callsignId,
      startTime: request.startTime,
      endTime: request.endTime,
    })
  }

  const handleSaveTiming = () => {
    if (!timingEditor) return
    const startMinutes = parseTimeToMinutes(timingEditor.startTime)
    const endMinutes = parseTimeToMinutes(timingEditor.endTime)
    if (startMinutes == null || endMinutes == null || endMinutes <= startMinutes) {
      trackOutcome('request_timing_updated', 'timeline', 'attempted', {
        interaction_method: 'modal',
      })
      trackOutcome('request_timing_updated', 'timeline', 'failed', {
        failure_type: 'validation',
        interaction_method: 'modal',
      })
      setTimingError(t('shiftTimeline.timingEditor.invalid'))
      return
    }

    commitRequestUpdate(timingEditor.requestId, startMinutes, endMinutes, timingEditor.callsignId)
    setTimingEditor(null)
    setTimingError('')
  }

  const handleOpenAssign = () => {
    setIsDetailsModalOpen(false)
    setIsAssignModalOpen(true)
  }

  const handleOpenQueueAssign = (request: Request) => {
    setAssignedCallsignName(undefined)
    setSelectedRequest(request)
    setIsAssignModalOpen(true)
  }

  const handleRequestAssign = (requestId: string, callsignId: string) => {
    const request = callsigns
      .flatMap((callsign) => callsign.requests)
      .find((item) => item.id === requestId)
    if (!request) return

    const startMinutes = parseTimeToMinutes(request.startTime)
    const endMinutes = parseTimeToMinutes(request.endTime)
    if (startMinutes == null || endMinutes == null) return

    commitRequestUpdate(requestId, startMinutes, endMinutes, callsignId, 'modal')
    setIsAssignModalOpen(false)
    setSelectedRequest(null)
  }

  const handleRequestUnassign = (requestId: string) => {
    const request = callsigns
      .flatMap((callsign) => callsign.requests)
      .find((item) => item.id === requestId)
    if (!request) return

    const startMinutes = parseTimeToMinutes(request.startTime)
    const endMinutes = parseTimeToMinutes(request.endTime)
    if (startMinutes == null || endMinutes == null) return

    commitRequestUpdate(requestId, startMinutes, endMinutes, UNASSIGNED_ROW_ID)
    setIsAssignModalOpen(false)
    setSelectedRequest(null)
  }

  useEffect(() => {
    setRequestOverrides({})
    setDraggingRequestId(null)
    setDropTargetRowId(null)
    activeDragRef.current = null
  }, [timelineQuery.data, selectedDateParam, selectedStationCode])

  useEffect(() => {
    setCollapsedRowIds(new Set())
  }, [selectedDateParam, selectedStationCode])

  const renderedCallsigns = useMemo(() => {
    const rowsById = new Map(
      callsigns.map((row) => [
        row.id,
        {
          ...row,
          requests: [] as typeof row.requests,
        },
      ])
    )

    for (const row of callsigns) {
      for (const request of row.requests) {
        const override = requestOverrides[request.id]
        const targetRow = rowsById.get(override?.rowId ?? row.id)
        if (!targetRow) continue

        targetRow.requests.push({
          ...request,
          ...(override
            ? {
                startTime: minutesToHHmm(override.startMinutes),
                endTime: minutesToHHmm(override.endMinutes),
              }
            : {}),
        })
      }
    }

    return callsigns.map((row) => rowsById.get(row.id) ?? row)
  }, [callsigns, requestOverrides])

  const breaksByCallsignId = useMemo(() => {
    const map = new Map<
      string,
      Array<{ id: string; startMinutes: number; endMinutes: number; reason?: string | null }>
    >()
    const breaks = timelineQuery.data?.callsign_breaks ?? []

    for (const breakItem of breaks) {
      const callsignId = breakItem.callsign?.id
      const startMinutes = parseTimeToMinutes(breakItem.start_time)
      const endMinutes = parseTimeToMinutes(breakItem.end_time)

      if (!callsignId || startMinutes == null || endMinutes == null || endMinutes <= startMinutes) {
        continue
      }

      const existing = map.get(callsignId) ?? []
      existing.push({
        id: breakItem.id,
        startMinutes,
        endMinutes,
        reason: breakItem.reason,
      })
      map.set(callsignId, existing)
    }

    return map
  }, [timelineQuery.data?.callsign_breaks])

  const visibleCallsigns = useMemo(() => {
    if (!hideOffShiftCallsigns) return renderedCallsigns

    const currentMinutes = currentNow.getHours() * 60 + currentNow.getMinutes()

    return renderedCallsigns.filter(
      (row) => row.id === UNASSIGNED_ROW_ID || isCallsignOnShift(row, currentMinutes)
    )
  }, [currentNow, hideOffShiftCallsigns, renderedCallsigns])
  const rowLayouts = useMemo(
    () => visibleCallsigns.map((row) => buildRowLayout(row, breaksByCallsignId.get(row.id) ?? [])),
    [breaksByCallsignId, visibleCallsigns]
  )
  const rowLayoutById = useMemo(
    () => new Map(visibleCallsigns.map((row, index) => [row.id, rowLayouts[index]])),
    [rowLayouts, visibleCallsigns]
  )
  const timelineCallsigns = useMemo(
    () =>
      prototypeLayout
        ? visibleCallsigns.filter((row) => row.id !== UNASSIGNED_ROW_ID)
        : visibleCallsigns,
    [prototypeLayout, visibleCallsigns]
  )
  const unassignedCallsign = callsigns.find((row) => row.id === UNASSIGNED_ROW_ID)
  const renderedUnassignedCallsign = visibleCallsigns.find((row) => row.id === UNASSIGNED_ROW_ID)
  const listCallsigns =
    prototypeLayout && renderedUnassignedCallsign
      ? [renderedUnassignedCallsign, ...timelineCallsigns]
      : visibleCallsigns
  const unassignedRequests = unassignedCallsign?.requests ?? EMPTY_REQUESTS
  const filteredUnassignedRequests = useMemo(() => {
    const query = unassignedQueueQuery.trim().toLowerCase()
    if (!query) return unassignedRequests

    return unassignedRequests.filter((request) =>
      [
        request.ref,
        request.customerName,
        request.headCode,
        request.boardStationCode,
        request.alightStationCode,
        request.type,
      ].some((value) => value?.toLowerCase().includes(query))
    )
  }, [unassignedQueueQuery, unassignedRequests])
  const visibleUnassignedRequests = filteredUnassignedRequests.slice(0, unassignedQueueLimit)
  const unassignedRequestWindows = useMemo(
    () =>
      new Map(
        (rowLayoutById.get(UNASSIGNED_ROW_ID)?.requests ?? []).map((item) => [
          item.request.id,
          item,
        ])
      ),
    [rowLayoutById]
  )

  const totalConflictCount = useMemo(
    () =>
      timelineCallsigns.reduce((sum, row) => {
        if (row.id === UNASSIGNED_ROW_ID) return sum
        const layout = rowLayoutById.get(row.id)
        return sum + (layout?.conflictPairCount ?? 0) + (layout?.breakConflictCount ?? 0)
      }, 0),
    [rowLayoutById, timelineCallsigns]
  )

  const totalCautionJobs = useMemo(
    () => callsigns.flatMap((c) => c.requests).filter((r) => r.status === 'CAUTION').length,
    [callsigns]
  )

  const rowVirtualizer = useVirtualizer({
    count: timelineCallsigns.length,
    getScrollElement: () => rowsScrollRef.current,
    estimateSize: (index) => {
      const row = timelineCallsigns[index]
      if (row && row.id !== UNASSIGNED_ROW_ID && collapsedRowIds.has(row.id)) {
        return ROW_LANE_HEIGHT + 1
      }
      const layout = rowLayoutById.get(timelineCallsigns[index]?.id)
      if (!layout) return ROW_LANE_HEIGHT + 1
      return Math.max(layout.laneCount, 1) * ROW_LANE_HEIGHT + 1 // +1 for border-bottom
    },
    overscan: draggingRequestId ? timelineCallsigns.length : 5,
    measureElement: (el) => el.getBoundingClientRect().height,
  })

  const windowSpan = windowEnd - windowStart
  const unassignedPreviewItems = useMemo(
    () =>
      (rowLayoutById.get(UNASSIGNED_ROW_ID)?.requests ?? [])
        .filter((item) => item.endMinutes > windowStart && item.startMinutes < windowEnd)
        .slice(0, UNASSIGNED_PREVIEW_LIMIT),
    [rowLayoutById, windowEnd, windowStart]
  )

  const segmentIntervalMinutes = useMemo(() => {
    const defaultDurationSeconds = stationSettingQuery.data?.default_assistance_job_duration
    if (!defaultDurationSeconds || defaultDurationSeconds <= 0) return 20
    return Math.max(5, Math.round(defaultDurationSeconds / 60))
  }, [stationSettingQuery.data?.default_assistance_job_duration])

  const ticks = useMemo(() => {
    const firstTick = Math.ceil(windowStart / segmentIntervalMinutes) * segmentIntervalMinutes
    const values: number[] = []
    for (let value = firstTick; value <= windowEnd; value += segmentIntervalMinutes) {
      values.push(value)
    }
    return values
  }, [segmentIntervalMinutes, windowEnd, windowStart])

  const setRowsScrollElement = useCallback((element: HTMLDivElement | null) => {
    rowsScrollResizeObserverRef.current?.disconnect()
    rowsScrollResizeObserverRef.current = null

    rowsScrollRef.current = element
    if (!element) {
      setScrollbarWidth(0)
      return
    }

    const updateScrollbarWidth = () => {
      setScrollbarWidth(element.offsetWidth - element.clientWidth)
    }

    updateScrollbarWidth()

    if (typeof ResizeObserver === 'undefined') {
      return
    }

    const resizeObserver = new ResizeObserver(updateScrollbarWidth)
    resizeObserver.observe(element)
    rowsScrollResizeObserverRef.current = resizeObserver
  }, [])

  useEffect(
    () => () => {
      rowsScrollResizeObserverRef.current?.disconnect()
      rowsScrollResizeObserverRef.current = null
    },
    []
  )

  const nowMinutes = currentNow.getHours() * 60 + currentNow.getMinutes()
  const showNowLine = shouldShowNowLine(filterDate, currentNow, windowStart, windowEnd)

  const handleDateChange = (value: string) => {
    setSelectedDateValue(value)
    if (value) {
      setFilterDateValue(value)
    }
  }

  const handleSearch = () => {
    if (selectedDateValue) {
      setFilterDateValue(selectedDateValue)
    }
    setFilterQuery(searchQuery.trim() || undefined)
    trackEvent('timeline_searched', { area: 'timeline' })
  }

  const onWindowStartChange = (value: number) => {
    const boundedStart = clampWindowStart(value, windowEnd)
    const boundedEnd = clampWindowEnd(windowEnd, boundedStart)
    setWindowStart(boundedStart)
    setWindowEnd(boundedEnd)
  }

  const onWindowEndChange = (value: number) => {
    const boundedEnd = clampWindowEnd(value, windowStart)
    const boundedStart = clampWindowStart(windowStart, boundedEnd)
    setWindowStart(boundedStart)
    setWindowEnd(boundedEnd)
  }

  const setWindowSpan = (candidateSpan: number) => {
    const span = clamp(candidateSpan, MIN_WINDOW_SPAN_MINUTES, MAX_WINDOW_SPAN_MINUTES)
    const centre = (windowStart + windowEnd) / 2
    const start = clamp(Math.round(centre - span / 2), 0, DAY_MINUTES - span)
    setWindowStart(start)
    setWindowEnd(start + span)
  }

  const handleJumpToNow = () => {
    const currentMinutes = currentNow.getHours() * 60 + currentNow.getMinutes()
    const start = clamp(Math.round(currentMinutes - windowSpan / 2), 0, DAY_MINUTES - windowSpan)

    setWindowStart(start)
    setWindowEnd(start + windowSpan)
    trackEvent('timeline_jump_to_now', { area: 'timeline' })
  }

  const startScrubberDrag = (event: React.PointerEvent, mode: ScrubberDragMode) => {
    const track = scrubberTrackRef.current
    if (!track) return

    const trackWidth = track.getBoundingClientRect().width
    if (trackWidth <= 0) return

    event.preventDefault()
    event.stopPropagation()

    const pointerStartX = event.clientX
    const initialStart = windowStart
    const initialEnd = windowEnd
    const initialSpan = initialEnd - initialStart

    const onMove = (moveEvent: PointerEvent) => {
      const deltaX = moveEvent.clientX - pointerStartX
      const deltaMinutes = pixelsToMinutes(deltaX, trackWidth, DAY_MINUTES)

      if (mode === 'pan') {
        const maxStart = DAY_MINUTES - initialSpan
        const nextStart = clamp(Math.round(initialStart + deltaMinutes), 0, maxStart)
        setWindowStart(nextStart)
        setWindowEnd(nextStart + initialSpan)
        return
      }

      if (mode === 'resize-left') {
        onWindowStartChange(initialStart + deltaMinutes)
        return
      }

      onWindowEndChange(initialEnd + deltaMinutes)
    }

    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const resolveRowIdFromPointer = (clientY: number): string | null => {
    for (const row of renderedCallsigns) {
      const element = rowElementRefs.current[row.id]
      if (!element) continue
      const rect = element.getBoundingClientRect()
      if (clientY >= rect.top && clientY <= rect.bottom) {
        return row.id
      }
    }

    return null
  }

  const clearDragState = () => {
    activeDragRef.current = null
    dragLaneFloorRef.current = {}
    setDraggingRequestId(null)
    setDropTargetRowId(null)
  }

  const startDrag = (
    event: React.PointerEvent,
    payload: {
      requestId: string
      mode: DragMode
      rowId: string
      startMinutes: number
      endMinutes: number
      anchorMinutes: number
      requestType: Request['type']
      lockedAxis?: 'vertical'
    }
  ) => {
    if (activeDragRef.current != null) return
    const target = rowContentRefs.current[payload.rowId]
    if (!target) return

    const contentWidth = target.getBoundingClientRect().width
    if (contentWidth <= 0) return

    event.preventDefault()
    event.stopPropagation()
    ;(event.currentTarget as Element).setPointerCapture?.(event.pointerId)

    const rowCallsign = renderedCallsigns.find((c) => c.id === payload.rowId)
    const rawShiftStartMinutes = parseTimeToMinutes(rowCallsign?.shiftStartTime)
    const rawShiftEndMinutes = parseTimeToMinutes(rowCallsign?.shiftEndTime)
    const shiftEndsNextDay =
      rowCallsign?.endsNextDay ||
      (rawShiftStartMinutes != null &&
        rawShiftEndMinutes != null &&
        rawShiftEndMinutes < rawShiftStartMinutes)
    const isAfterMidnight =
      shiftEndsNextDay && rawShiftEndMinutes != null && payload.startMinutes < rawShiftEndMinutes
    const shiftStartMinutes = isAfterMidnight ? 0 : rawShiftStartMinutes
    const shiftEndMinutes = shiftEndsNextDay
      ? isAfterMidnight
        ? rawShiftEndMinutes
        : DAY_MINUTES
      : rawShiftEndMinutes

    const dragStart: ActiveDrag = {
      requestId: payload.requestId,
      mode: payload.mode,
      axis: payload.mode === 'move' ? null : 'horizontal',
      lockedAxis: payload.lockedAxis,
      startClientX: event.clientX,
      startClientY: event.clientY,
      contentWidth,
      originRowId: payload.rowId,
      originStartMinutes: payload.startMinutes,
      originEndMinutes: payload.endMinutes,
      anchorMinutes: payload.anchorMinutes,
      requestType: payload.requestType,
      draftRowId: payload.rowId,
      draftStartMinutes: payload.startMinutes,
      draftEndMinutes: payload.endMinutes,
      moved: false,
      shiftStartMinutes,
      shiftEndMinutes,
    }

    activeDragRef.current = dragStart
    dragLaneFloorRef.current = Object.fromEntries(
      visibleCallsigns.map((row) => [row.id, rowLayoutById.get(row.id)?.laneCount ?? 1])
    )
    setDraggingRequestId(payload.requestId)
    setDropTargetRowId(null)

    const onMove = (moveEvent: PointerEvent) => {
      const activeDrag = activeDragRef.current
      if (!activeDrag) return

      const deltaX = moveEvent.clientX - activeDrag.startClientX
      const deltaY = moveEvent.clientY - activeDrag.startClientY

      if (activeDrag.mode === 'move' && activeDrag.axis == null) {
        if (Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD) return
        activeDrag.axis = activeDrag.lockedAxis ?? determineDragAxis(deltaX, deltaY)
        activeDrag.moved = true
      }

      const deltaMinutes = pixelsToMinutes(deltaX, activeDrag.contentWidth, windowSpan)

      if (activeDrag.mode === 'move' && activeDrag.axis === 'vertical') {
        const rowId = resolveRowIdFromPointer(moveEvent.clientY) ?? activeDrag.originRowId

        setDropTargetRowId(rowId)
        activeDrag.moved = true
        activeDrag.draftRowId = rowId
        activeDrag.draftStartMinutes = activeDrag.originStartMinutes
        activeDrag.draftEndMinutes = activeDrag.originEndMinutes
        setRequestOverrides((previous) => ({
          ...previous,
          [activeDrag.requestId]: {
            rowId,
            startMinutes: activeDrag.originStartMinutes,
            endMinutes: activeDrag.originEndMinutes,
          },
        }))

        return
      }

      if (activeDrag.mode === 'resize-left') {
        const rawStart = activeDrag.originStartMinutes + deltaMinutes
        const startMinutes = clampResizedStart(
          rawStart,
          activeDrag.originEndMinutes,
          activeDrag.anchorMinutes,
          resizeDurationBounds,
          activeDrag.shiftStartMinutes
        )
        activeDrag.moved = true
        activeDrag.draftRowId = activeDrag.originRowId
        activeDrag.draftStartMinutes = startMinutes
        activeDrag.draftEndMinutes = activeDrag.originEndMinutes

        setRequestOverrides((previous) => ({
          ...previous,
          [activeDrag.requestId]: {
            rowId: activeDrag.originRowId,
            startMinutes,
            endMinutes: activeDrag.originEndMinutes,
          },
        }))

        return
      }

      if (activeDrag.mode === 'resize-right') {
        const rawEnd = activeDrag.originEndMinutes + deltaMinutes
        const endMinutes = clampResizedEnd(
          activeDrag.originStartMinutes,
          rawEnd,
          activeDrag.anchorMinutes,
          resizeDurationBounds,
          activeDrag.shiftEndMinutes,
          activeDrag.requestType
        )
        activeDrag.moved = true
        activeDrag.draftRowId = activeDrag.originRowId
        activeDrag.draftStartMinutes = activeDrag.originStartMinutes
        activeDrag.draftEndMinutes = endMinutes

        setRequestOverrides((previous) => ({
          ...previous,
          [activeDrag.requestId]: {
            rowId: activeDrag.originRowId,
            startMinutes: activeDrag.originStartMinutes,
            endMinutes,
          },
        }))

        return
      }

      const duration = activeDrag.originEndMinutes - activeDrag.originStartMinutes
      const rawStart = activeDrag.originStartMinutes + deltaMinutes
      const nextWindow = clampMovedWindow(rawStart, duration, activeDrag.anchorMinutes)

      setDropTargetRowId(null)
      activeDrag.moved = true
      activeDrag.draftRowId = activeDrag.originRowId
      activeDrag.draftStartMinutes = nextWindow.start
      activeDrag.draftEndMinutes = nextWindow.end
      setRequestOverrides((previous) => ({
        ...previous,
        [activeDrag.requestId]: {
          rowId: activeDrag.originRowId,
          startMinutes: nextWindow.start,
          endMinutes: nextWindow.end,
        },
      }))
    }

    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      const completedDrag = activeDragRef.current

      if (completedDrag?.moved) {
        suppressNextClickByRequestIdRef.current[completedDrag.requestId] = true
        commitRequestUpdate(
          completedDrag.requestId,
          completedDrag.draftStartMinutes,
          completedDrag.draftEndMinutes,
          completedDrag.draftRowId,
          'drag_drop'
        )
      }

      if (completedDrag) {
        setRequestOverrides((previous) => {
          const next = { ...previous }
          delete next[completedDrag.requestId]
          return next
        })
      }

      clearDragState()
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
  }

  const renderUnassignedTray = () => (
    <section className="stp-prototype-unassigned" aria-labelledby="prototype-unassigned-heading">
      <div className="stp-prototype-unassigned-label">
        <strong id="prototype-unassigned-heading">
          {t('shiftTimeline.prototype.unassignedCount', { count: unassignedRequests.length })}
        </strong>
        <button
          type="button"
          className="stp-prototype-queue-toggle"
          onClick={() => setIsUnassignedQueueOpen((open) => !open)}
          aria-expanded={isUnassignedQueueOpen}
          aria-controls="prototype-unassigned-queue"
        >
          {isUnassignedQueueOpen
            ? t('shiftTimeline.prototype.closeQueue')
            : t('shiftTimeline.prototype.openQueue')}
        </button>
      </div>
      <div
        className="stp-prototype-unassigned-track"
        ref={(element) => {
          rowContentRefs.current[UNASSIGNED_ROW_ID] = element
        }}
      >
        {ticks.map((tick) => (
          <span
            aria-hidden="true"
            key={`unassigned-hour-${tick}`}
            className="stp-hour-line"
            style={{ left: `${((tick - windowStart) / windowSpan) * 100}%` }}
          />
        ))}
        {unassignedPreviewItems.map(({ request, startMinutes, endMinutes, anchorMinutes }) => {
          const visibleStart = Math.max(startMinutes, windowStart)
          const visibleEnd = Math.min(endMinutes, windowEnd)
          const leftPercent = ((visibleStart - windowStart) / windowSpan) * 100
          const widthPercent = ((visibleEnd - visibleStart) / windowSpan) * 100
          const typeClass = request.type === 'DEP' ? 'stp-job-dep' : 'stp-job-arr'

          return (
            <button
              type="button"
              key={`unassigned-preview-${request.id}`}
              className={`stp-job stp-prototype-preview ${typeClass}`}
              style={{
                left: `${leftPercent}%`,
                width: `${Math.max(widthPercent, 4)}%`,
              }}
              onPointerDown={(event) =>
                timelineCallsigns.length > 0 &&
                startDrag(event, {
                  requestId: request.id,
                  mode: 'move',
                  rowId: UNASSIGNED_ROW_ID,
                  startMinutes,
                  endMinutes,
                  anchorMinutes,
                  requestType: request.type,
                  lockedAxis: 'vertical',
                })
              }
              onClick={() => handleRequestClick(request.id)}
              aria-label={`${request.ref ?? request.id}. ${request.customerName ?? t('timeline.unknownPassenger')}. ${request.startTime}–${request.endTime}`}
            >
              <span className="stp-job-inner">
                <span className="stp-job-text">
                  <span className="stp-job-station">{getRequestStationCode(request.label)}</span>
                  <span className="stp-job-passenger">
                    {request.customerName ?? request.ref ?? request.id}
                  </span>
                </span>
              </span>
            </button>
          )
        })}
        {unassignedRequests.length > unassignedPreviewItems.length && (
          <span className="stp-prototype-unassigned-more">
            +{unassignedRequests.length - unassignedPreviewItems.length}{' '}
            {t('shiftTimeline.prototype.more')}
          </span>
        )}
      </div>
    </section>
  )

  const renderUnassignedQueue = () => (
    <aside
      id="prototype-unassigned-queue"
      className="stp-prototype-queue"
      aria-labelledby="prototype-unassigned-queue-heading"
    >
      <div className="stp-prototype-queue-header">
        <h2 id="prototype-unassigned-queue-heading">
          {t('shiftTimeline.prototype.queueTitle', { count: unassignedRequests.length })}
        </h2>
        <button
          type="button"
          className="stp-prototype-queue-close"
          onClick={() => setIsUnassignedQueueOpen(false)}
          aria-label={t('shiftTimeline.prototype.closeQueue')}
        >
          ×
        </button>
      </div>

      {timelineCallsigns.length === 0 && (
        <p role="status" className="stp-prototype-queue-notice">
          {t('shiftTimeline.prototype.allocationUnavailable')}
        </p>
      )}

      <div className="stp-prototype-queue-search">
        <label htmlFor="prototype-unassigned-search">
          {t('shiftTimeline.prototype.searchLabel')}
        </label>
        <input
          id="prototype-unassigned-search"
          type="search"
          value={unassignedQueueQuery}
          onChange={(event) => {
            setUnassignedQueueQuery(event.target.value)
            setUnassignedQueueLimit(UNASSIGNED_QUEUE_BATCH_SIZE)
          }}
          placeholder={t('shiftTimeline.prototype.searchPlaceholder')}
        />
      </div>

      <p role="status" aria-live="polite" className="stp-prototype-queue-count">
        {t('shiftTimeline.prototype.showing', {
          shown: visibleUnassignedRequests.length,
          total: filteredUnassignedRequests.length,
        })}
      </p>

      {visibleUnassignedRequests.length === 0 ? (
        <p className="stp-prototype-queue-empty">{t('shiftTimeline.prototype.noMatches')}</p>
      ) : (
        <ul className="stp-prototype-queue-list">
          {visibleUnassignedRequests.map((request) => {
            const requestWindow = unassignedRequestWindows.get(request.id)
            const route = [request.boardStationCode, request.alightStationCode]
              .filter(Boolean)
              .join(' → ')

            return (
              <li key={`unassigned-queue-${request.id}`} className="stp-prototype-queue-item">
                <button
                  type="button"
                  className="stp-prototype-queue-request"
                  onPointerDown={(event) => {
                    if (timelineCallsigns.length === 0 || !requestWindow) return
                    startDrag(event, {
                      requestId: request.id,
                      mode: 'move',
                      rowId: UNASSIGNED_ROW_ID,
                      startMinutes: requestWindow.startMinutes,
                      endMinutes: requestWindow.endMinutes,
                      anchorMinutes: requestWindow.anchorMinutes,
                      requestType: request.type,
                      lockedAxis: 'vertical',
                    })
                  }}
                  onClick={() => handleRequestClick(request.id)}
                >
                  <span className="stp-prototype-queue-time">{request.startTime}</span>
                  <span
                    className={`stp-prototype-queue-type ${request.type === 'ARR' ? 'stp-prototype-queue-type-arr' : 'stp-prototype-queue-type-dep'}`}
                  >
                    {request.type}
                  </span>
                  <span className="stp-prototype-queue-name">
                    {request.customerName ?? request.ref ?? request.id}
                  </span>
                  {route && <span className="stp-prototype-queue-route">{route}</span>}
                </button>
                <AppButton
                  variant="outline"
                  size="sm"
                  disabled={timelineCallsigns.length === 0}
                  onClick={() => handleOpenQueueAssign(request)}
                >
                  {t('requestAssign.assign')}
                </AppButton>
              </li>
            )
          })}
        </ul>
      )}

      {visibleUnassignedRequests.length < filteredUnassignedRequests.length && (
        <AppButton
          variant="outline"
          size="sm"
          onClick={() => setUnassignedQueueLimit((limit) => limit + UNASSIGNED_QUEUE_BATCH_SIZE)}
        >
          {t('shiftTimeline.prototype.showMore', {
            count: Math.min(
              UNASSIGNED_QUEUE_BATCH_SIZE,
              filteredUnassignedRequests.length - visibleUnassignedRequests.length
            ),
          })}
        </AppButton>
      )}
    </aside>
  )

  if (timelineQuery.isLoading) return <ShiftTimelineSkeleton />

  return (
    <div className="stp-page">
      <h1 className="sr-only">{t('shiftTimeline.title')}</h1>
      <div className="stp-controls">
        <div className="stp-control-row">
          <label htmlFor="timeline-date">{t('shiftTimeline.dateLabel')}</label>
          <input
            id="timeline-date"
            type="date"
            value={selectedDateValue}
            onChange={(event) => handleDateChange(event.target.value)}
          />
        </div>

        <div className="stp-control-row stp-control-grow">
          <label htmlFor="timeline-search">{t('shiftTimeline.searchLabel')}</label>
          <input
            id="timeline-search"
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={t('shiftTimeline.searchPlaceholder')}
          />
        </div>

        <AppButton variant="primary" size="sm" onClick={handleSearch}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <Search size={14} aria-hidden="true" />
            {t('shiftTimeline.searchButton')}
          </span>
        </AppButton>
        <AppButton
          variant="outline"
          size="sm"
          buttonClass="stp-callsign-toggle"
          ariaPressed={hideOffShiftCallsigns}
          onClick={() => {
            const nextHideOffShiftCallsigns = !hideOffShiftCallsigns
            setHideOffShiftCallsigns(nextHideOffShiftCallsigns)
            trackEvent('callsign_visibility_changed', {
              area: 'timeline',
              callsign_visibility: nextHideOffShiftCallsigns ? 'on_shift' : 'all',
            })
          }}
        >
          {hideOffShiftCallsigns
            ? t('shiftTimeline.showAllCallsigns')
            : t('shiftTimeline.hideOffshiftCallsigns')}
        </AppButton>
        <AppButton
          variant="outline"
          size="sm"
          onClick={() => {
            setEditingBreak(null)
            setIsAddBreakModalOpen(true)
          }}
        >
          {t('shiftTimeline.addBreak')}
        </AppButton>

        {viewMode === 'visual' && (
          <AppButton
            variant="outline"
            size="sm"
            disabled={!isSameDay(filterDate, currentNow)}
            onClick={handleJumpToNow}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <Clock size={12} aria-hidden="true" />
              {t('shiftTimeline.jumpToNow')}
            </span>
          </AppButton>
        )}

        {prototypeLayout && (
          <div
            className="stp-prototype-zoom"
            role="group"
            aria-label={t('shiftTimeline.prototype.zoomControls')}
          >
            <AppButton
              variant="outline"
              size="sm"
              disabled={windowSpan >= MAX_WINDOW_SPAN_MINUTES}
              onClick={() => setWindowSpan(windowSpan * 2)}
            >
              {t('shiftTimeline.prototype.zoomOut')}
            </AppButton>
            <span role="status" aria-live="polite">
              {t('shiftTimeline.prototype.zoomWindow', { count: windowSpan / 60 })}
            </span>
            <AppButton
              variant="outline"
              size="sm"
              disabled={windowSpan <= MIN_WINDOW_SPAN_MINUTES}
              onClick={() => setWindowSpan(windowSpan / 2)}
            >
              {t('shiftTimeline.prototype.zoomIn')}
            </AppButton>
          </div>
        )}

        <button
          type="button"
          className={`stp-refresh-toggle ${isPaused ? 'stp-refresh-toggle-paused' : 'stp-refresh-toggle-active'}`}
          onClick={() => setIsPaused((prev) => !prev)}
          aria-pressed={!isPaused}
          aria-label={
            isPaused ? t('shiftTimeline.autoRefreshOff') : t('shiftTimeline.autoRefreshOn')
          }
        >
          <span className="stp-refresh-icon" aria-hidden="true">
            {isPaused ? (
              <Play size={14} aria-hidden="true" />
            ) : (
              <Pause size={14} aria-hidden="true" />
            )}
          </span>
          <span className="stp-refresh-text">{t('shiftTimeline.autoRefreshLabel')}</span>
          <span className="stp-refresh-dot" aria-hidden="true" />
        </button>
      </div>

      {viewMode === 'visual' && (
        <div className={`stp-visual-view ${prototypeLayout ? 'stp-visual-view-prototype' : ''}`}>
          <div className="stp-axis-row">
            <div className="stp-label-spacer stp-prototype-axis-label">
              {prototypeLayout && t('shiftTimeline.prototype.callsignStaff')}
            </div>
            <div
              aria-hidden="true"
              className="stp-axis-track"
              style={{ width: `calc(100% - var(--stp-label-width) - ${scrollbarWidth}px)` }}
            >
              {ticks.map((tick) => {
                const isHourTick = tick % 60 === 0

                return (
                  <div
                    key={tick}
                    className="stp-axis-tick"
                    style={{ left: `${((tick - windowStart) / windowSpan) * 100}%` }}
                  >
                    <div
                      className={`stp-axis-tick-line ${isHourTick ? 'stp-axis-tick-major' : 'stp-axis-tick-minor'}`}
                    />
                    <div
                      className={`stp-axis-tick-label ${isHourTick ? 'stp-axis-tick-label-major' : 'stp-axis-tick-label-minor'}`}
                    >
                      {minutesToHHmm(tick)}
                    </div>
                  </div>
                )
              })}

              {showNowLine && (
                <div
                  className="stp-now-line"
                  style={{ left: `${((nowMinutes - windowStart) / windowSpan) * 100}%` }}
                />
              )}
            </div>
          </div>

          {prototypeLayout && renderUnassignedTray()}

          {timelineQuery.error ? (
            <div role="alert" className="stp-state stp-state-error">
              {t('shiftTimeline.errorLoading')}
            </div>
          ) : !selectedStationCode ? (
            <div className="stp-state">{t('common.selectStationFirst')}</div>
          ) : (
            <div
              role="region"
              className="stp-rows-scroll"
              ref={setRowsScrollElement}
              aria-label={t('shiftTimeline.view.visualRegionLabel')}
              tabIndex={0}
            >
              {prototypeLayout && timelineCallsigns.length === 0 && (
                <div className="stp-prototype-empty-scheduler">
                  <h2>{t('shiftTimeline.prototype.noCallsignsTitle')}</h2>
                  <p>{t('shiftTimeline.prototype.noCallsignsDescription')}</p>
                  <Link to="/shifts" className="stp-prototype-manage-shifts">
                    {t('shiftTimeline.prototype.manageShifts')}
                  </Link>
                </div>
              )}

              {showNowLine && (
                <div className="stp-now-line-overlay-track" aria-hidden="true">
                  <span
                    className="stp-now-line stp-now-line-rows-overlay"
                    style={{ left: `${((nowMinutes - windowStart) / windowSpan) * 100}%` }}
                  />
                </div>
              )}

              <div
                className="stp-rows-scroll-content"
                style={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const row = timelineCallsigns[virtualRow.index]
                  if (!row) return null
                  const rowLayout = rowLayoutById.get(row.id)
                  if (!rowLayout) return null
                  const isUnassigned = row.id === UNASSIGNED_ROW_ID
                  const isCollapsed = !isUnassigned && collapsedRowIds.has(row.id)
                  const crossoverGroups = isCollapsed
                    ? buildLaneCrossovers(rowLayout.requests, windowStart, windowEnd)
                    : []
                  const crossoverRequestIds = new Set(
                    crossoverGroups.flatMap((group) => group.requestIds)
                  )
                  const laneFloor = draggingRequestId ? (dragLaneFloorRef.current[row.id] ?? 0) : 0
                  const rowHeight = isCollapsed
                    ? ROW_LANE_HEIGHT
                    : Math.max(rowLayout.laneCount, laneFloor) * ROW_LANE_HEIGHT
                  const highlightDropRow = dropTargetRowId === row.id
                  const shiftStartMinutes = parseTimeToMinutes(row.shiftStartTime)
                  const shiftEndMinutes = parseTimeToMinutes(row.shiftEndTime)
                  const endsNextDay =
                    row.endsNextDay ||
                    (shiftStartMinutes != null &&
                      shiftEndMinutes != null &&
                      shiftEndMinutes < shiftStartMinutes)
                  const hasVisibleWorkingHours =
                    row.id !== UNASSIGNED_ROW_ID &&
                    shiftStartMinutes != null &&
                    shiftEndMinutes != null &&
                    shiftEndMinutes !== shiftStartMinutes
                  const offShiftWindows = hasVisibleWorkingHours
                    ? endsNextDay
                      ? [{ start: shiftEndMinutes, end: shiftStartMinutes }]
                      : [
                          { start: 0, end: shiftStartMinutes },
                          { start: shiftEndMinutes, end: DAY_MINUTES },
                        ]
                    : []

                  return (
                    <div
                      key={row.id}
                      ref={rowVirtualizer.measureElement}
                      data-index={virtualRow.index}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      <div
                        className={`stp-row ${row.id === UNASSIGNED_ROW_ID ? 'stp-row-unassigned' : ''} ${isCollapsed ? 'stp-row-collapsed' : ''} ${highlightDropRow ? 'stp-row-drop-target' : ''}`}
                        style={{ minHeight: `${rowHeight}px` }}
                        role="group"
                        aria-labelledby={`timeline-row-${row.id}`}
                        ref={(element) => {
                          rowElementRefs.current[row.id] = element
                        }}
                      >
                        <div className="stp-row-label" style={{ minHeight: `${rowHeight}px` }}>
                          {!isUnassigned && (
                            <button
                              type="button"
                              className="stp-row-toggle"
                              aria-label={t(
                                isCollapsed
                                  ? 'shiftTimeline.view.expandLane'
                                  : 'shiftTimeline.view.collapseLane',
                                { callsign: row.name }
                              )}
                              aria-expanded={!isCollapsed}
                              aria-controls={`timeline-lane-content-${row.id}`}
                              onClick={() => setLaneCollapsed(row.id, !isCollapsed)}
                            >
                              <span aria-hidden="true">{isCollapsed ? '+' : '-'}</span>
                            </button>
                          )}
                          <div className="stp-row-label-details">
                            <span id={`timeline-row-${row.id}`} className="stp-row-callsign">
                              {row.name}
                            </span>
                            <span className="stp-row-meta">
                              {row.staffName && (
                                <span className="stp-row-staff">{row.staffName}</span>
                              )}
                              {hasVisibleWorkingHours && (
                                <span className="stp-row-shift-time">
                                  {row.shiftStartTime} - {row.shiftEndTime}
                                  {endsNextDay && ` · ${t('resources.overnight')}`}
                                </span>
                              )}
                            </span>
                            <span className="stp-row-assistance-count">
                              {t('shiftTimeline.view.laneAssistanceCount', {
                                count: row.requests.length,
                              })}
                            </span>
                          </div>
                        </div>

                        <div
                          id={`timeline-lane-content-${row.id}`}
                          className={`stp-row-content ${isCollapsed ? 'stp-row-content-collapsed' : ''}`}
                          style={{ minHeight: `${rowHeight}px` }}
                          ref={(element) => {
                            rowContentRefs.current[row.id] = element
                          }}
                        >
                          {crossoverGroups.map((group) => {
                            const count = group.requestIds.length
                            return (
                              <button
                                key={`${group.startMinutes}-${group.endMinutes}`}
                                type="button"
                                className="stp-crossover-indicator"
                                aria-label={t('shiftTimeline.view.expandCrossover', {
                                  callsign: row.name,
                                  count,
                                })}
                                title={t('shiftTimeline.view.crossoverCount', { count })}
                                style={{
                                  left: `${((group.startMinutes - windowStart) / windowSpan) * 100}%`,
                                  width: `${((group.endMinutes - group.startMinutes) / windowSpan) * 100}%`,
                                }}
                                onClick={() => setLaneCollapsed(row.id, false)}
                              >
                                <span aria-hidden="true">
                                  {t('shiftTimeline.view.crossoverBadge', { count })}
                                </span>
                              </button>
                            )
                          })}
                          {offShiftWindows.map((window) => {
                            const start = Math.max(window.start, windowStart)
                            const end = Math.min(window.end, windowEnd)
                            if (end <= start) return null

                            return (
                              <span
                                aria-hidden="true"
                                key={`${window.start}-${window.end}`}
                                className="stp-off-shift-region"
                                style={{
                                  left: `${((start - windowStart) / windowSpan) * 100}%`,
                                  width: `${((end - start) / windowSpan) * 100}%`,
                                }}
                              />
                            )
                          })}

                          {endsNextDay && (
                            <>
                              <span
                                role="img"
                                aria-label={t('resources.continuesFromPreviousDay')}
                                className="stp-shift-continuation stp-shift-continuation-leading"
                                style={{ left: `${((0 - windowStart) / windowSpan) * 100}%` }}
                              >
                                {Array.from({ length: 4 }, (_, index) => (
                                  <ChevronLeft key={index} aria-hidden="true" />
                                ))}
                              </span>
                              <span
                                role="img"
                                aria-label={t('resources.continuesNextDay')}
                                className="stp-shift-continuation stp-shift-continuation-trailing"
                                style={{
                                  left: `${((DAY_MINUTES - windowStart) / windowSpan) * 100}%`,
                                }}
                              >
                                {Array.from({ length: 4 }, (_, index) => (
                                  <ChevronRight key={index} aria-hidden="true" />
                                ))}
                              </span>
                            </>
                          )}

                          {(breaksByCallsignId.get(row.id) ?? []).map((breakWindow) => {
                            if (
                              breakWindow.endMinutes <= windowStart ||
                              breakWindow.startMinutes >= windowEnd
                            ) {
                              return null
                            }

                            const visibleBreakStart = Math.max(
                              breakWindow.startMinutes,
                              windowStart
                            )
                            const visibleBreakEnd = Math.min(breakWindow.endMinutes, windowEnd)
                            const breakLeft = ((visibleBreakStart - windowStart) / windowSpan) * 100
                            const breakWidth =
                              ((visibleBreakEnd - visibleBreakStart) / windowSpan) * 100

                            const startLabel = minutesToHHmm(breakWindow.startMinutes)
                            const endLabel = minutesToHHmm(breakWindow.endMinutes)
                            const reasonTrimmed = breakWindow.reason?.trim()
                            const ariaBreak = t('shiftTimeline.view.editBreak', {
                              callsign: row.name,
                            })
                            const ariaTimes = t('shiftTimeline.breakTooltip.timeRange', {
                              start: startLabel,
                              end: endLabel,
                            })
                            const ariaReason = reasonTrimmed
                              ? `${t('shiftTimeline.breakTooltip.reason')}: ${reasonTrimmed}`
                              : ''
                            const ariaLabel = [ariaBreak, ariaTimes, ariaReason]
                              .filter(Boolean)
                              .join('. ')

                            return (
                              <AppTooltip
                                key={`${row.id}-break-${breakWindow.id}`}
                                content={
                                  draggingRequestId ? undefined : (
                                    <BreakTooltipContent
                                      startTime={startLabel}
                                      endTime={endLabel}
                                      reason={breakWindow.reason}
                                    />
                                  )
                                }
                                position="top"
                              >
                                <button
                                  type="button"
                                  aria-label={ariaLabel}
                                  className="stp-break-region"
                                  style={{
                                    left: `${breakLeft}%`,
                                    width: `${breakWidth}%`,
                                  }}
                                  onClick={(event) => {
                                    event.preventDefault()
                                    event.stopPropagation()
                                    setEditingBreak({
                                      id: breakWindow.id,
                                      callsignId: row.id,
                                      startTime: startLabel,
                                      endTime: endLabel,
                                      reason: breakWindow.reason,
                                    })
                                    setIsAddBreakModalOpen(true)
                                  }}
                                />
                              </AppTooltip>
                            )
                          })}

                          {ticks.map((tick) => (
                            <span
                              aria-hidden="true"
                              key={`${row.id}-hour-${tick}`}
                              className={`stp-hour-line ${tick % 60 === 0 ? 'stp-hour-line-major' : ''}`}
                              style={{ left: `${((tick - windowStart) / windowSpan) * 100}%` }}
                            />
                          ))}

                          {rowLayout.requests.map(
                            ({ request, startMinutes, endMinutes, anchorMinutes }) => {
                              if (endMinutes <= windowStart || startMinutes >= windowEnd) {
                                return null
                              }

                              const visibleStart = Math.max(startMinutes, windowStart)
                              const visibleEnd = Math.min(endMinutes, windowEnd)
                              const laneIndex = rowLayout.laneByRequestId.get(request.id) ?? 0

                              const leftPercent = ((visibleStart - windowStart) / windowSpan) * 100
                              const widthPercent = ((visibleEnd - visibleStart) / windowSpan) * 100
                              const anchorOffsetPercent =
                                ((anchorMinutes - visibleStart) /
                                  (visibleEnd - visibleStart || 1)) *
                                100

                              const conflict = shouldShowRequestConflictStyling(
                                request,
                                row.id,
                                rowLayout.conflictRequestIds
                              )
                              const typeClass =
                                request.type === 'DEP' ? 'stp-job-dep' : 'stp-job-arr'
                              const caution = request.status === 'CAUTION'
                              const noConflict = request.status === 'NO_CONFLICT'
                              const dragging = draggingRequestId === request.id
                              const inCrossoverGroup = crossoverRequestIds.has(request.id)
                              const linkedBookingId = request.bookingId?.trim()
                              const linkedHighlight =
                                Boolean(request.linkedJobContext && linkedBookingId) &&
                                activeLinkedBookingId === linkedBookingId
                              const stationCode = getRequestStationCode(request.label)
                              const realtimeLabels = [
                                isJourneyLate(request.realtimeClassification)
                                  ? t('journeyIndicators.lateAria')
                                  : '',
                                hasPlatformChangeSignal(
                                  request.realtimeClassification,
                                  request.platform
                                )
                                  ? t('journeyIndicators.platformChangedAria')
                                  : '',
                              ].filter(Boolean)
                              const requestAriaLabel = [
                                request.ref ?? request.id,
                                request.customerName ?? t('timeline.unknownPassenger'),
                                request.type === 'ARR'
                                  ? t('timeline.legend.arrival')
                                  : t('timeline.legend.departure'),
                                `${request.startTime}–${request.endTime}`,
                                t('shiftTimeline.view.assignedCallsign', {
                                  callsign: row.name,
                                }),
                                request.scheduledTime
                                  ? t('shiftTimeline.view.scheduledTrainTime', {
                                      time: request.scheduledTime,
                                    })
                                  : '',
                                request.platform
                                  ? t('shiftTimeline.view.platformValue', {
                                      platform: request.platform,
                                    })
                                  : '',
                                request.status,
                                conflict ? t('shiftTimeline.view.conflict') : '',
                                request.linkedJobContext ? t('linkedJobs.title') : '',
                                ...realtimeLabels,
                              ]
                                .filter(Boolean)
                                .join('. ')

                              return (
                                <AppTooltip
                                  key={request.id}
                                  content={
                                    draggingRequestId ? undefined : (
                                      <RequestTooltipContent request={request} />
                                    )
                                  }
                                  position="top"
                                >
                                  <button
                                    type="button"
                                    aria-label={requestAriaLabel}
                                    className={`stp-job ${typeClass} ${caution ? 'stp-job-caution' : ''} ${noConflict ? 'stp-job-no-conflict' : ''} ${conflict ? 'stp-job-conflict' : ''} ${dragging ? 'stp-job-dragging' : ''} ${linkedHighlight ? 'stp-job-linked-highlight' : ''} ${isCollapsed && !inCrossoverGroup ? 'stp-job-collapsed-visible' : ''}`}
                                    style={{
                                      left: `${leftPercent}%`,
                                      width: `${Math.max(widthPercent, 1)}%`,
                                      top: `${(isCollapsed ? 0 : laneIndex) * ROW_LANE_HEIGHT + JOB_PADDING}px`,
                                      height: `${ROW_LANE_HEIGHT - JOB_PADDING * 2}px`,
                                    }}
                                    onPointerDown={(event) => {
                                      const target = event.target as Element
                                      const mode = target.closest('.stp-resize-left')
                                        ? 'resize-left'
                                        : target.closest('.stp-resize-right')
                                          ? 'resize-right'
                                          : 'move'
                                      startDrag(event, {
                                        requestId: request.id,
                                        mode,
                                        rowId: row.id,
                                        startMinutes,
                                        endMinutes,
                                        anchorMinutes,
                                        requestType: request.type,
                                      })
                                    }}
                                    onClick={() => handleRequestClick(request.id)}
                                    onPointerEnter={() => {
                                      if (request.linkedJobContext && linkedBookingId) {
                                        setActiveLinkedBookingId(linkedBookingId)
                                      }
                                    }}
                                    onPointerLeave={() => {
                                      if (activeLinkedBookingId === linkedBookingId) {
                                        setActiveLinkedBookingId(null)
                                      }
                                    }}
                                    onFocus={() => {
                                      if (request.linkedJobContext && linkedBookingId) {
                                        setActiveLinkedBookingId(linkedBookingId)
                                      }
                                    }}
                                    onBlur={() => {
                                      if (activeLinkedBookingId === linkedBookingId) {
                                        setActiveLinkedBookingId(null)
                                      }
                                    }}
                                  >
                                    <span
                                      aria-hidden="true"
                                      className="stp-resize-handle stp-resize-left"
                                    />
                                    <span
                                      aria-hidden="true"
                                      className="stp-resize-handle stp-resize-right"
                                    />
                                    <span
                                      aria-hidden="true"
                                      className="stp-anchor"
                                      style={{ left: `${clamp(anchorOffsetPercent, 0, 100)}%` }}
                                    />
                                    <div className="stp-job-inner">
                                      <div className="stp-job-text">
                                        <span className="stp-job-station">{stationCode}</span>
                                        {request.customerName && (
                                          <span className="stp-job-passenger">
                                            {request.customerName}
                                          </span>
                                        )}
                                        {request.linkedJobContext && (
                                          <Link2
                                            size={13}
                                            aria-hidden="true"
                                            className="stp-job-linked"
                                          />
                                        )}
                                        <RequestRealtimeIndicators request={request} />
                                      </div>
                                    </div>
                                  </button>
                                </AppTooltip>
                              )
                            }
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div className="stp-footer">
            <div className="stp-scrubber-zone">
              <div className="stp-label-spacer" />
              <div
                className="stp-scrubber-content"
                style={{ width: `calc(100% - var(--stp-label-width) - ${scrollbarWidth}px)` }}
              >
                <div className="stp-scrubber-track" ref={scrubberTrackRef}>
                  {MINIMAP_TICKS.map((tick) => (
                    <span
                      key={`minimap-${tick}`}
                      className="stp-minimap-tick"
                      style={{ left: `${(tick / DAY_MINUTES) * 100}%` }}
                    />
                  ))}

                  <div
                    className="stp-scrubber-fill"
                    role="group"
                    aria-label={t('timeline.visibleWindow')}
                    style={{
                      left: `${(windowStart / DAY_MINUTES) * 100}%`,
                      width: `${(windowSpan / DAY_MINUTES) * 100}%`,
                    }}
                    onPointerDown={(event) => startScrubberDrag(event, 'pan')}
                  >
                    <span
                      className="stp-scrubber-handle stp-scrubber-handle-left"
                      onPointerDown={(event) => startScrubberDrag(event, 'resize-left')}
                    />
                    <span
                      className="stp-scrubber-handle stp-scrubber-handle-right"
                      onPointerDown={(event) => startScrubberDrag(event, 'resize-right')}
                    />
                  </div>
                </div>

                <div className="stp-scrubber-label-row">
                  <span>00:00</span>
                  <span>24:00</span>
                </div>
              </div>
            </div>

            <div className="stp-legend-bar">
              <div className="stp-legend-item">
                <span className="stp-swatch stp-swatch-dep" />
                <span>{t('timeline.legend.departure')}</span>
              </div>
              <div className="stp-legend-item">
                <span className="stp-swatch stp-swatch-arr" />
                <span>{t('timeline.legend.arrival')}</span>
              </div>
              <div className="stp-legend-item">
                <span className="stp-swatch stp-swatch-break" />
                <span>{t('shiftTimeline.legend.break')}</span>
              </div>
              <div className="stp-legend-item">
                <span className="stp-swatch stp-swatch-caution" />
                <span>{t('shiftTimeline.legend.caution')}</span>
              </div>
              <div className="stp-legend-item">
                <span className="stp-swatch stp-swatch-no-conflict" />
                <span>{t('shiftTimeline.legend.noConflict')}</span>
              </div>
              <div className="stp-legend-item">
                <span className="stp-swatch stp-swatch-conflict" />
                <span>{t('shiftTimeline.legend.conflictingRequests')}</span>
              </div>
              <div className="stp-legend-item">
                <span className="stp-attention-mark">!</span>
                <span>{t('shiftTimeline.legend.requiresAttention')}</span>
              </div>
              <div className="stp-legend-pills">
                {totalCautionJobs > 0 && (
                  <div className="stp-caution-pill">
                    {t('timeline.legend.cautions', { count: totalCautionJobs })}
                  </div>
                )}
                <div className="stp-conflict-pill">
                  {t('timeline.legend.conflicts', { count: totalConflictCount })}
                </div>
              </div>
            </div>
          </div>
          {prototypeLayout && isUnassignedQueueOpen && renderUnassignedQueue()}
        </div>
      )}

      {viewMode === 'list' && (
        <section
          aria-labelledby="timeline-list-heading"
          className="stp-list-view"
          aria-busy={timelineQuery.isLoading}
        >
          <div className="stp-list-heading-row">
            <div>
              <h2 id="timeline-list-heading">{t('shiftTimeline.view.listHeading')}</h2>
              <p>{t('shiftTimeline.view.listDescription')}</p>
            </div>
            <p role="status" aria-live="polite" className="stp-list-count">
              {t('shiftTimeline.view.resultCount', {
                count: listCallsigns.reduce((sum, row) => sum + row.requests.length, 0),
              })}
            </p>
          </div>

          {prototypeLayout && (
            <div className="stp-prototype-queue-search stp-prototype-list-search">
              <label htmlFor="prototype-unassigned-list-search">
                {t('shiftTimeline.prototype.searchLabel')}
              </label>
              <input
                id="prototype-unassigned-list-search"
                type="search"
                value={unassignedQueueQuery}
                onChange={(event) => {
                  setUnassignedQueueQuery(event.target.value)
                  setUnassignedQueueLimit(UNASSIGNED_QUEUE_BATCH_SIZE)
                }}
                placeholder={t('shiftTimeline.prototype.searchPlaceholder')}
              />
            </div>
          )}

          {timelineQuery.isLoading ? (
            <p role="status" className="stp-state">
              {t('common.loading')}
            </p>
          ) : timelineQuery.error ? (
            <p role="alert" className="stp-state stp-state-error">
              {t('shiftTimeline.errorLoading')}
            </p>
          ) : !selectedStationCode ? (
            <p className="stp-state">{t('common.selectStationFirst')}</p>
          ) : (
            <div className="stp-list-lanes">
              {prototypeLayout && timelineCallsigns.length === 0 && (
                <section className="stp-list-lane stp-prototype-list-empty">
                  <h2>{t('shiftTimeline.prototype.noCallsignsTitle')}</h2>
                  <p>{t('shiftTimeline.prototype.noCallsignsDescription')}</p>
                  <Link to="/shifts" className="stp-prototype-manage-shifts">
                    {t('shiftTimeline.prototype.manageShifts')}
                  </Link>
                </section>
              )}
              {listCallsigns.map((row) => {
                const rowBreaks = breaksByCallsignId.get(row.id) ?? []
                const rowRequests =
                  prototypeLayout && row.id === UNASSIGNED_ROW_ID
                    ? visibleUnassignedRequests
                    : row.requests
                return (
                  <section
                    key={`list-${row.id}`}
                    className="stp-list-lane"
                    aria-labelledby={`timeline-list-lane-${row.id}`}
                  >
                    <div className="stp-list-lane-heading">
                      <h3 id={`timeline-list-lane-${row.id}`}>{row.name}</h3>
                      <p>
                        {row.staffName || t('resources.agenda.unassigned')}
                        {row.shiftStartTime && row.shiftEndTime
                          ? ` · ${row.shiftStartTime}–${row.shiftEndTime}`
                          : ''}
                      </p>
                    </div>

                    {rowRequests.length === 0 && rowBreaks.length === 0 ? (
                      <p className="stp-list-empty">{t('shiftTimeline.view.noLaneItems')}</p>
                    ) : (
                      <ul className="stp-list-items">
                        {rowRequests.map((request) => {
                          const conflict = shouldShowRequestConflictStyling(
                            request,
                            row.id,
                            rowLayoutById.get(row.id)?.conflictRequestIds ?? new Set<string>()
                          )
                          return (
                            <li key={`list-request-${request.id}`}>
                              <article className="stp-list-card">
                                <h4>
                                  {request.ref ?? request.id} —{' '}
                                  {request.customerName ?? t('timeline.unknownPassenger')}
                                </h4>
                                <dl className="stp-list-details">
                                  <div>
                                    <dt>{t('shiftTimeline.view.type')}</dt>
                                    <dd>{request.type}</dd>
                                  </div>
                                  <div>
                                    <dt>{t('shiftTimeline.view.assistanceTime')}</dt>
                                    <dd>
                                      {request.startTime}–{request.endTime}
                                    </dd>
                                  </div>
                                  <div>
                                    <dt>{t('shiftTimeline.view.trainTime')}</dt>
                                    <dd>{request.scheduledTime || '—'}</dd>
                                  </div>
                                  <div>
                                    <dt>{t('timeline.tooltip.platform')}</dt>
                                    <dd>{request.platform || '—'}</dd>
                                  </div>
                                  <div>
                                    <dt>{t('shiftTimeline.view.status')}</dt>
                                    <dd>
                                      {[
                                        request.status,
                                        conflict ? t('shiftTimeline.view.conflict') : '',
                                      ]
                                        .filter(Boolean)
                                        .join(', ') || '—'}
                                    </dd>
                                  </div>
                                </dl>
                                <div className="stp-list-actions">
                                  <AppButton
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleRequestClick(request.id)}
                                  >
                                    {t('shiftTimeline.view.manageAssignment', {
                                      reference: request.ref ?? request.id,
                                    })}
                                  </AppButton>
                                  <AppButton
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handleOpenTimingEditor(request, row.id)}
                                  >
                                    {t('shiftTimeline.view.editTimes', {
                                      reference: request.ref ?? request.id,
                                    })}
                                  </AppButton>
                                </div>
                              </article>
                            </li>
                          )
                        })}

                        {prototypeLayout &&
                          row.id === UNASSIGNED_ROW_ID &&
                          visibleUnassignedRequests.length < filteredUnassignedRequests.length && (
                            <li>
                              <AppButton
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setUnassignedQueueLimit(
                                    (limit) => limit + UNASSIGNED_QUEUE_BATCH_SIZE
                                  )
                                }
                              >
                                {t('shiftTimeline.prototype.showMore', {
                                  count: Math.min(
                                    UNASSIGNED_QUEUE_BATCH_SIZE,
                                    filteredUnassignedRequests.length -
                                      visibleUnassignedRequests.length
                                  ),
                                })}
                              </AppButton>
                            </li>
                          )}

                        {rowBreaks.map((breakWindow) => {
                          const startTime = minutesToHHmm(breakWindow.startMinutes)
                          const endTime = minutesToHHmm(breakWindow.endMinutes)
                          return (
                            <li key={`list-break-${breakWindow.id}`}>
                              <article className="stp-list-card stp-list-break-card">
                                <h4>{t('shiftTimeline.breakTooltip.title')}</h4>
                                <p>
                                  {startTime}–{endTime}
                                </p>
                                {breakWindow.reason?.trim() && <p>{breakWindow.reason}</p>}
                                <AppButton
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setEditingBreak({
                                      id: breakWindow.id,
                                      callsignId: row.id,
                                      startTime,
                                      endTime,
                                      reason: breakWindow.reason,
                                    })
                                    setIsAddBreakModalOpen(true)
                                  }}
                                >
                                  {t('shiftTimeline.view.editBreak', { callsign: row.name })}
                                </AppButton>
                              </article>
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </section>
                )
              })}
            </div>
          )}
        </section>
      )}

      <AssistanceDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false)
          setSelectedRequest(null)
        }}
        request={selectedRequest}
        callsigns={callsigns}
        onAssignClick={handleOpenAssign}
        stationCode={selectedStationCode}
      />

      <RequestDetailModal
        isOpen={isAssignModalOpen}
        onClose={() => {
          setIsAssignModalOpen(false)
          setSelectedRequest(null)
        }}
        request={selectedRequest}
        assignedCallsignName={assignedCallsignName}
        callsigns={callsigns}
        onAssign={handleRequestAssign}
        onUnassign={handleRequestUnassign}
      />

      <Modal
        isOpen={timingEditor !== null}
        onClose={() => {
          setTimingEditor(null)
          setTimingError('')
        }}
        title={t('shiftTimeline.timingEditor.title', {
          reference: timingEditor?.requestRef ?? '',
        })}
        size="sm"
      >
        {timingEditor && (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="timeline-edit-start-time"
                className="mb-1 block text-sm font-medium text-text-gray-700"
              >
                {t('shiftTimeline.timingEditor.start')}
              </label>
              <input
                id="timeline-edit-start-time"
                type="time"
                value={timingEditor.startTime}
                aria-invalid={Boolean(timingError)}
                aria-describedby={timingError ? 'timeline-timing-error' : undefined}
                onChange={(event) =>
                  setTimingEditor((current) =>
                    current ? { ...current, startTime: event.target.value } : current
                  )
                }
                className="w-full rounded border border-border-gray px-3 py-2"
              />
            </div>
            <div>
              <label
                htmlFor="timeline-edit-end-time"
                className="mb-1 block text-sm font-medium text-text-gray-700"
              >
                {t('shiftTimeline.timingEditor.end')}
              </label>
              <input
                id="timeline-edit-end-time"
                type="time"
                value={timingEditor.endTime}
                aria-invalid={Boolean(timingError)}
                aria-describedby={timingError ? 'timeline-timing-error' : undefined}
                onChange={(event) =>
                  setTimingEditor((current) =>
                    current ? { ...current, endTime: event.target.value } : current
                  )
                }
                className="w-full rounded border border-border-gray px-3 py-2"
              />
            </div>
            {timingError && (
              <p id="timeline-timing-error" role="alert" className="text-sm text-danger">
                {timingError}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <AppButton variant="outline" size="sm" onClick={() => setTimingEditor(null)}>
                {t('common.cancel')}
              </AppButton>
              <AppButton variant="primary" size="sm" onClick={handleSaveTiming}>
                {t('common.save')}
              </AppButton>
            </div>
          </div>
        )}
      </Modal>

      <AddBreakModal
        isOpen={isAddBreakModalOpen}
        onClose={() => {
          setIsAddBreakModalOpen(false)
          setEditingBreak(null)
        }}
        callsigns={callsigns}
        date={filterDate}
        initialBreak={editingBreak}
        onBreakCreated={() => {
          setIsAddBreakModalOpen(false)
          setEditingBreak(null)
          queryClient.invalidateQueries({ queryKey: shiftTimelineQueryKey })
        }}
        onBreakUpdated={() => {
          setIsAddBreakModalOpen(false)
          setEditingBreak(null)
          queryClient.invalidateQueries({ queryKey: shiftTimelineQueryKey })
        }}
        onBreakDeleted={() => {
          setIsAddBreakModalOpen(false)
          setEditingBreak(null)
          queryClient.invalidateQueries({ queryKey: shiftTimelineQueryKey })
        }}
      />
    </div>
  )
}

export const TimelinePrototypePage = () => <TimelinePage prototypeLayout />
